/**
 * Whether a Day-scale timeline column should be treated as non-working
 * (weekend / holiday / exception) for greying.
 *
 * Prefer DHTMLX `isWorkTime` with `unit: 'day'` (same calendars as Live Schedule).
 * Calling isWorkTime on the scale cell's midnight date without a unit checks whether
 * 00:00 is inside working hours — that is false for every day when hours start at 08:00,
 * so all columns would look the same and weekoffs would not stand out.
 * Fallback: Sat/Sun.
 */
export type GanttWorkTimeApi = {
  isWorkTime?: (
    config:
      | Date
      | {
          date: Date;
          unit?: string;
          task?: unknown;
        },
    time_unit?: string,
  ) => boolean;
};

export function isGanttNonWorkingScaleDay(
  gantt: GanttWorkTimeApi | null | undefined,
  date: Date,
): boolean {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return false;
  if (typeof gantt?.isWorkTime === 'function') {
    try {
      // Day unit: any working hours that day → working column.
      return gantt.isWorkTime({ date, unit: 'day' }) === false;
    } catch {
      try {
        return gantt.isWorkTime(date, 'day') === false;
      } catch {
        // fall through to weekday fallback
      }
    }
  }
  const day = date.getDay(); // 0 = Sunday, 6 = Saturday
  return day === 0 || day === 6;
}

/**
 * CSS for the Day *unit* scale row (`scales[].css`) and timeline cells.
 *
 * Do not use `applyNonWorking` from the global `scale_cell_class` template —
 * that template runs for every scale row (Month above Day) and would grey month headers.
 */
export function ganttDayScaleCellClasses(params: {
  date: Date;
  zoomLevel: string;
  gantt?: GanttWorkTimeApi | null;
  isToday: boolean;
  kind: 'scale' | 'timeline';
  /** When false, only today tint is applied (for shared scale_cell_class). Default true. */
  applyNonWorking?: boolean;
}): string {
  if (params.zoomLevel !== 'day') {
    return params.isToday
      ? params.kind === 'scale'
        ? 'gantt-scale-today'
        : 'gantt-timeline-today'
      : '';
  }
  const parts: string[] = [];
  const applyNonWorking = params.applyNonWorking !== false;
  if (applyNonWorking && isGanttNonWorkingScaleDay(params.gantt, params.date)) {
    parts.push(
      params.kind === 'scale' ? 'gantt-scale-nonworking' : 'gantt-timeline-nonworking',
    );
  }
  if (params.isToday) {
    parts.push(params.kind === 'scale' ? 'gantt-scale-today' : 'gantt-timeline-today');
  }
  return parts.join(' ');
}
